import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { productAPI } from '../lib/api';
import { useStore } from '../context/StoreContext';
import { useCart } from '../context/CartContext';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Scissors,
  ShoppingCart,
  Plus,
  Minus,
  Package,
  Search
} from 'lucide-react';
import { Input } from '@/components/ui/input';

export default function ShopPage() {
  const { slug } = useParams();
  const { store, selectStore, hasShop } = useStore();
  const { items, addItem, getItemCount, openCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, [slug]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (!store || store.slug !== slug) {
        await selectStore(slug);
      }
      
      const response = await productAPI.list();
      setProducts(response.data);
    } catch (error) {
      console.error('Errore caricamento prodotti:', error);
      if (error.response?.status === 403) {
        toast.error('Lo shop non è disponibile per questo negozio');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addItem(product);
    toast.success(`${product.name} aggiunto al carrello`);
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!hasShop) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <Package className="w-16 h-16 text-muted-foreground mb-4" />
        <h1 className="text-2xl font-bold mb-2">Shop non disponibile</h1>
        <p className="text-muted-foreground mb-6 text-center">
          Questo negozio non ha ancora attivato lo shop prodotti.
        </p>
        <Button asChild>
          <Link to={`/s/${slug}`}>Torna al Negozio</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to={`/s/${slug}`} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Scissors className="w-6 h-6 text-primary" />
              <span className="font-semibold">{store?.name} - Shop</span>
            </div>
          </div>
          
          <Button 
            variant="outline" 
            className="relative"
            asChild
            data-testid="cart-btn"
          >
            <Link to={`/s/${slug}/carrello`}>
              <ShoppingCart className="w-5 h-5" />
              {getItemCount() > 0 && (
                <span className="cart-badge">{getItemCount()}</span>
              )}
            </Link>
          </Button>
        </div>
      </header>

      <div className="pt-24 pb-12 section-padding">
        <div className="container-main">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">I Nostri Prodotti</h1>
            <p className="text-muted-foreground mb-8">
              Scopri la nostra selezione di prodotti professionali
            </p>
            
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Cerca prodotti..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-products"
              />
            </div>
          </div>
          
          {/* Products Grid */}
          {filteredProducts.length === 0 ? (
            <Card className="p-12 text-center">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchQuery ? 'Nessun prodotto trovato per la tua ricerca.' : 'Nessun prodotto disponibile al momento.'}
              </p>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <Card 
                  key={product.id} 
                  className="product-card overflow-hidden"
                  data-testid={`product-${product.id}`}
                >
                  <div className="aspect-square bg-muted relative overflow-hidden">
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="product-image"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-16 h-16 text-muted-foreground/50" />
                      </div>
                    )}
                    {product.stock <= 5 && product.stock > 0 && (
                      <Badge className="absolute top-2 right-2" variant="secondary">
                        Ultime {product.stock}
                      </Badge>
                    )}
                    {product.stock === 0 && (
                      <Badge className="absolute top-2 right-2" variant="destructive">
                        Esaurito
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-1">{product.name}</h3>
                    {product.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {product.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-primary">
                        {formatPrice(product.price)}
                      </span>
                      <Button 
                        size="sm"
                        onClick={() => handleAddToCart(product)}
                        disabled={product.stock === 0}
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Floating Cart Button (Mobile) */}
      {getItemCount() > 0 && (
        <div className="fixed bottom-4 left-4 right-4 md:hidden">
          <Button 
            className="w-full gap-2" 
            size="lg"
            asChild
          >
            <Link to={`/s/${slug}/carrello`}>
              <ShoppingCart className="w-5 h-5" />
              Vai al Carrello ({getItemCount()})
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
